
import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAppContext } from '../contexts/AppContext';
import { useNotifications } from '../contexts/NotificationContext';
import { UserRole, SettingsFormData } from '../types';
import Button from '../components/ui/Button';
import Card from '../components/ui/Card';
import {
  UsersIcon, LockClosedIcon, BellAlertIcon, WalletIcon, BuildingOfficeIcon,
  PhoneIcon, AdjustmentsHorizontalIcon, ChartPieIcon, UserCircleIcon, PencilSquareIcon,
  TagIcon
} from '@heroicons/react/24/outline';
import { useTranslation } from 'react-i18next';
import Tabs from '../components/ui/Tabs';

// Section specific components
import ContactBookingSettings from '../components/settings/sections/ContactBookingSettings';
import NotificationPreferencesSettings from '../components/settings/sections/NotificationPreferencesSettings';
import DefaultsSettings from '../components/settings/sections/DefaultsSettings';
import BillingSubscriptionSettings from '../components/settings/sections/BillingSubscriptionSettings';
import AnalyticsPreferencesSettings from '../components/settings/sections/AnalyticsPreferencesSettings';
import TeamPermissionsSettings from '../components/settings/sections/TeamPermissionsSettings';
import PrivacyDataSettings from '../components/settings/sections/PrivacyDataSettings';
import AccountSecuritySettings from '../components/settings/sections/AccountSecuritySettings';
import PromoCodeManagerSettings from '../components/settings/sections/PromoCodeManagerSettings';
import { useAuthenticatedApi } from '../hooks/useAuthenticatedApi';


export interface SettingsSectionConfig {
  id: string;
  nameKey: string;
  icon: React.ElementType;
  component: React.FC<{ settings: SettingsFormData; onChange: (field: keyof SettingsFormData, value: any) => void; userRole: UserRole }>;
  roles: UserRole[];
}

const SettingsPage: React.FC = () => {
  const { t } = useTranslation(['settings', 'common']);
  const { currentUser } = useAppContext();
  const { addNotification } = useNotifications();
  const { request } = useAuthenticatedApi();
  const navigate = useNavigate();
  const location = useLocation();

  const [formData, setFormData] = useState<SettingsFormData | null>(null);
  const [initialFormData, setInitialFormData] = useState<SettingsFormData | null>(null);
  const [activeSectionId, setActiveSectionId] = useState<string>('');
  const [isDirty, setIsDirty] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const sectionRefs = useRef<Record<string, HTMLDivElement | null>>({});

  useEffect(() => {
    if (!currentUser) {
      setFormData(null);
      setInitialFormData(null);
      return;
    }

    let cancelled = false;

    const loadSettings = async () => {
      setIsLoading(true);
      try {
        let roleSettings: Partial<SettingsFormData> = {};

        if (currentUser.role === UserRole.FOUNDATION) {
          const response = await request<{ success: boolean; data?: any }>('/settings/foundation');
          const data = response.success && response.data ? response.data : ({} as any);
          const org = currentUser.primaryOrganization;
          roleSettings = {
            companyName: data.companyName || org?.name || currentUser.orgName || '',
            contactEmail: data.contactEmail || currentUser.email,
            phoneNumber: data.phoneNumber || org?.phoneNumber || '',
            contactPerson: data.contactPerson || org?.contactPerson || '',
            websiteUrl: data.websiteUrl || org?.websiteUrl || '',
            address: data.address || org?.region || '',
            canton: data.canton || org?.canton || '',
            city: data.city || org?.city || '',
            regionsServed: Array.isArray(data.regionsServed) ? data.regionsServed : (Array.isArray(org?.regionsServed) ? org.regionsServed : (org?.canton ? [org.canton] : [])),
            languagesSpoken: Array.isArray(data.languages) ? data.languages : (org?.languages || []),
            aboutText: data.description || org?.description || '',
            description: data.description || org?.description || '',
            vatNumber: data.vatNumber || org?.vatNumber || '',
            logoUrl: data.logoUrl || org?.logoUrl || null,
            coverImageUrl: data.coverImageUrl || org?.coverImageUrl || null,
            capacity: typeof data.capacity === 'number' ? data.capacity : (org?.capacity || 0),
            pedagogy: Array.isArray(data.pedagogy) ? data.pedagogy : (org?.pedagogy || []),
          } as Partial<SettingsFormData>;
        } else if (currentUser.role === UserRole.PRODUCT_SUPPLIER) {
          const response = await request<{ success: boolean; data?: any }>('/settings/supplier');
          const data = response.success && response.data ? response.data : ({} as any);
          const org = currentUser.primaryOrganization;
          roleSettings = {
            companyName: data.companyName || org?.name || '',
            contactEmail: data.contactEmail || currentUser.email,
            phoneNumber: data.phoneNumber || org?.phoneNumber || '',
            contactPerson: data.contactPerson || org?.contactPerson || '',
            websiteUrl: data.websiteUrl || org?.websiteUrl || '',
            address: data.address || org?.region || '',
            canton: data.canton || org?.canton || '',
            city: data.city || org?.city || '',
            regionsServed: Array.isArray(data.regionsServed) ? data.regionsServed : (Array.isArray(org?.regionsServed) ? org.regionsServed : (org?.canton ? [org.canton] : [])),
            languagesSpoken: Array.isArray(data.languages) ? data.languages : (org?.languages || []),
            aboutText: data.description || org?.description || '',
            description: data.description || org?.description || '',
            vatNumber: data.vatNumber || org?.vatNumber || '',
            logoUrl: data.logoUrl || org?.logoUrl || null,
            coverImageUrl: data.coverImageUrl || org?.coverImageUrl || null,
            productCategory: data.productCategory || org?.productCategory || '',
            serviceType: data.serviceType || org?.serviceType || '',
            minimumOrderQuantity:
              Number.isFinite(data.minimumOrderQuantity)
                ? data.minimumOrderQuantity
                : Number.isFinite(org?.minimumOrderQuantity)
                  ? org?.minimumOrderQuantity
                  : undefined,
            directOrderLink: data.directOrderLink || org?.directOrderLink || '',
            catalogUrl: data.catalogUrl || org?.catalogUrl || '',
          } as Partial<SettingsFormData>;
        } else if (currentUser.role === UserRole.SERVICE_PROVIDER) {
          const response = await request<{ success: boolean; data?: any }>('/settings/service-provider');
          const data = response.success && response.data ? response.data : ({} as any);
          const org = currentUser.primaryOrganization;
          roleSettings = {
            companyName: data.companyName || org?.name || '',
            contactEmail: data.contactEmail || currentUser.email,
            phoneNumber: data.phoneNumber || org?.phoneNumber || '',
            contactPerson: data.contactPerson || org?.contactPerson || '',
            websiteUrl: data.websiteUrl || org?.websiteUrl || '',
            address: data.address || org?.region || '',
            canton: data.canton || org?.canton || '',
            city: data.city || org?.city || '',
            regionsServed: Array.isArray(data.regionsServed) ? data.regionsServed : (Array.isArray(org?.regionsServed) ? org.regionsServed : (org?.canton ? [org.canton] : [])),
            languagesSpoken: Array.isArray(data.languages) ? data.languages : (org?.languages || []),
            aboutText: data.description || org?.description || '',
            description: data.description || org?.description || '',
            vatNumber: data.vatNumber || org?.vatNumber || '',
            logoUrl: data.logoUrl || org?.logoUrl || null,
            coverImageUrl: data.coverImageUrl || org?.coverImageUrl || null,
            serviceType: data.serviceType || org?.serviceType || '',
            serviceCategories: Array.isArray(data.serviceCategories) ? data.serviceCategories : (org?.serviceCategories || []),
            deliveryType: data.deliveryType || org?.deliveryType || '',
            bookingLink: data.bookingLink || org?.bookingLink || '',
          } as Partial<SettingsFormData>;
        }

        const [privacyResponse, notificationResponse] = await Promise.all([
          request<{ success: boolean; data?: any }>('/settings/privacy'),
          request<{ success: boolean; data?: any }>('/settings/notifications'),
        ]);

        const privacyData = privacyResponse.success && privacyResponse.data
          ? {
              hidePubliclyToggle: !!(privacyResponse.data as any).hidePubliclyToggle,
              gdprDataDeletionRequestMade: !!(privacyResponse.data as any).gdprDataDeletionRequestMade,
            }
          : { hidePubliclyToggle: false, gdprDataDeletionRequestMade: false };

        const notificationData = notificationResponse.success && notificationResponse.data
          ? {
              newRequestEmailToggle: !!(notificationResponse.data as any).newRequestEmailToggle,
              digestRadio: ((notificationResponse.data as any).digestRadio as SettingsFormData['digestRadio']) || 'Daily',
              promoRedemptionAlertsToggle: !!(notificationResponse.data as any).promoRedemptionAlertsToggle,
            }
          : { newRequestEmailToggle: true, digestRadio: 'Daily' as const, promoRedemptionAlertsToggle: false };

        if (cancelled) {
          return;
        }

        const combined = {
          ...roleSettings,
          ...privacyData,
          ...notificationData,
        } as SettingsFormData;

        setFormData(combined);
        setInitialFormData(JSON.parse(JSON.stringify(combined)));
      } catch (error) {
        if (!cancelled) {
          console.error('Failed to load settings', error);
          addNotification({
            title: t('common:errors.genericErrorTitle'),
            message: t('common:errors.genericErrorMessage'),
            type: 'error',
          });
          setFormData(null);
          setInitialFormData(null);
        }
      } finally {
        if (!cancelled) {
          setIsLoading(false);
        }
      }
    };

    loadSettings();

    return () => {
      cancelled = true;
    };
  }, [currentUser, request, addNotification, t]);

  useEffect(() => {
    if (formData && initialFormData) {
      setIsDirty(JSON.stringify(formData) !== JSON.stringify(initialFormData));
    } else {
      setIsDirty(false);
    }
  }, [formData, initialFormData]);

  // Note: ProfileDocumentsSettings (Catalogs & Documents) has been moved to the Edit Profile page
  // for Product Suppliers and Service Providers
  const sections: SettingsSectionConfig[] = [
    { id: 'accountSecurity', nameKey: 'common:settingsPage.accountSecurity', icon: UserCircleIcon, component: AccountSecuritySettings, roles: [UserRole.PRODUCT_SUPPLIER, UserRole.SERVICE_PROVIDER, UserRole.FOUNDATION, UserRole.EDUCATOR, UserRole.PARENT, UserRole.ADMIN, UserRole.SUPER_ADMIN] },
    { id: 'billingSubscription', nameKey: 'common:settingsPage.billingSubscription', icon: WalletIcon, component: BillingSubscriptionSettings, roles: [UserRole.PRODUCT_SUPPLIER, UserRole.SERVICE_PROVIDER, UserRole.FOUNDATION] },
    { id: 'promoCodeManager', nameKey: 'common:settingsPage.promoCodeManager', icon: TagIcon, component: PromoCodeManagerSettings, roles: [UserRole.PRODUCT_SUPPLIER, UserRole.SERVICE_PROVIDER] },
    { id: 'notifications', nameKey: 'common:settingsPage.notificationPreferences', icon: BellAlertIcon, component: NotificationPreferencesSettings, roles: [UserRole.PRODUCT_SUPPLIER, UserRole.FOUNDATION] },
    { id: 'privacyData', nameKey: 'common:settingsPage.privacyData', icon: LockClosedIcon, component: PrivacyDataSettings, roles: [UserRole.PRODUCT_SUPPLIER, UserRole.SERVICE_PROVIDER, UserRole.FOUNDATION] },
    { id: 'contactBooking', nameKey: 'common:settingsPage.contactBooking', icon: PhoneIcon, component: ContactBookingSettings, roles: [UserRole.PRODUCT_SUPPLIER] },
    { id: 'analyticsPreferences', nameKey: 'common:settingsPage.analyticsPreferences', icon: ChartPieIcon, component: AnalyticsPreferencesSettings, roles: [UserRole.PRODUCT_SUPPLIER] },
  ];

  const availableSections = sections.filter(section => currentUser && section.roles.includes(currentUser.role));

  useEffect(() => {
    if (availableSections.length > 0 && !activeSectionId) {
      setActiveSectionId(availableSections[0].id);
    }
  }, [availableSections, activeSectionId]);

  // Deep-link support: /settings#billingSubscription (optionally ?focus=manage-subscription)
  useEffect(() => {
    if (!currentUser) return;
    const hashSectionId = (location.hash || '').replace('#', '').trim();
    if (!hashSectionId) return;

    const hasSection = availableSections.some(s => s.id === hashSectionId);
    if (!hasSection) return;

    // Activate tab / section
    setActiveSectionId(hashSectionId);

    // Scroll for non-tab layout (most roles)
    if (currentUser.role !== UserRole.FOUNDATION) {
      requestAnimationFrame(() => {
        sectionRefs.current[hashSectionId]?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      });
    }
  }, [location.hash, currentUser, availableSections]);

  const handleFormChange = (field: keyof SettingsFormData, value: any) => {
    setFormData(prev => (prev ? { ...prev, [field]: value } : null));
  };

  const handleSave = async () => {
    if (!formData || !currentUser) {
      return;
    }

    setIsSaving(true);
    try {
      const requests: Promise<any>[] = [];
      const payload = formData as Record<string, any>;

      if (currentUser.role === UserRole.FOUNDATION) {
        requests.push(
          request('/settings/foundation', {
            method: 'PATCH',
            body: JSON.stringify({
              companyName: payload.companyName || currentUser.orgName || '',
              contactEmail: payload.contactEmail || currentUser.email,
              phoneNumber: payload.phoneNumber || '',
              contactPerson: payload.contactPerson || '',
              websiteUrl: payload.websiteUrl || '',
              address: payload.address || '',
              canton: payload.canton || '',
              city: payload.city || '',
              regionsServed: Array.isArray(payload.regionsServed) ? payload.regionsServed : (payload.canton ? [payload.canton] : []),
              description: payload.aboutText || payload.description || '',
              vatNumber: payload.vatNumber || '',
              languages: Array.isArray(payload.languagesSpoken) ? payload.languagesSpoken : [],
              capacity: Number.isFinite(payload.capacity) ? Number(payload.capacity) : 0,
              pedagogy: Array.isArray(payload.pedagogy) ? payload.pedagogy : [],
              ...(payload.logoAssetId !== undefined && { logoAssetId: payload.logoAssetId || null }),
              ...(payload.coverAssetId !== undefined && { coverAssetId: payload.coverAssetId || null }),
            }),
          })
        );
      }

      if (currentUser.role === UserRole.PRODUCT_SUPPLIER) {
        requests.push(
          request('/settings/supplier', {
            method: 'PATCH',
            body: JSON.stringify({
              companyName: payload.companyName || '',
              contactEmail: payload.contactEmail || currentUser.email,
              phoneNumber: payload.phoneNumber || '',
              contactPerson: payload.contactPerson || '',
              websiteUrl: payload.websiteUrl || '',
              address: payload.address || '',
              canton: payload.canton || '',
              city: payload.city || '',
              regionsServed: Array.isArray(payload.regionsServed) ? payload.regionsServed : (payload.canton ? [payload.canton] : []),
              description: payload.aboutText || payload.description || '',
              vatNumber: payload.vatNumber || '',
              languages: Array.isArray(payload.languagesSpoken) ? payload.languagesSpoken : [],
              productCategory: payload.productCategory || '',
              serviceType: payload.serviceType || '',
              minimumOrderQuantity: Number.isFinite(payload.minimumOrderQuantity)
                ? Number(payload.minimumOrderQuantity)
                : null,
              directOrderLink: payload.directOrderLink || '',
              catalogUrl: payload.catalogUrl || '',
              ...(payload.logoAssetId !== undefined && { logoAssetId: payload.logoAssetId || null }),
              ...(payload.coverAssetId !== undefined && { coverAssetId: payload.coverAssetId || null }),
            }),
          })
        );
      }

      if (currentUser.role === UserRole.SERVICE_PROVIDER) {
        requests.push(
          request('/settings/service-provider', {
            method: 'PATCH',
            body: JSON.stringify({
              companyName: payload.companyName || '',
              contactEmail: payload.contactEmail || currentUser.email,
              phoneNumber: payload.phoneNumber || '',
              contactPerson: payload.contactPerson || '',
              websiteUrl: payload.websiteUrl || '',
              address: payload.address || '',
              canton: payload.canton || '',
              city: payload.city || '',
              regionsServed: Array.isArray(payload.regionsServed) ? payload.regionsServed : (payload.canton ? [payload.canton] : []),
              description: payload.aboutText || payload.description || '',
              vatNumber: payload.vatNumber || '',
              languages: Array.isArray(payload.languagesSpoken) ? payload.languagesSpoken : [],
              serviceType: payload.serviceType || '',
              serviceCategories: Array.isArray(payload.serviceCategories) ? payload.serviceCategories : [],
              deliveryType: payload.deliveryType || '',
              bookingLink: payload.bookingLink || '',
              ...(payload.logoAssetId !== undefined && { logoAssetId: payload.logoAssetId || null }),
              ...(payload.coverAssetId !== undefined && { coverAssetId: payload.coverAssetId || null }),
            }),
          })
        );
      }


      requests.push(
        request('/settings/privacy', {
          method: 'PATCH',
          body: JSON.stringify({
            hidePubliclyToggle: !!payload.hidePubliclyToggle,
            gdprDataDeletionRequestMade: !!payload.gdprDataDeletionRequestMade,
          }),
        })
      );

      requests.push(
        request('/settings/notifications', {
          method: 'PATCH',
          body: JSON.stringify({
            newRequestEmailToggle: !!payload.newRequestEmailToggle,
            digestRadio: (payload.digestRadio as 'Daily' | 'Weekly' | 'None') || 'Daily',
            promoRedemptionAlertsToggle: !!payload.promoRedemptionAlertsToggle,
          }),
        })
      );

      const responses = await Promise.all(requests);
      const failure = responses.find(res => res && res.success === false);
      if (failure) {
        throw new Error(failure.message || 'Failed to save settings');
      }

      setInitialFormData(JSON.parse(JSON.stringify(formData)));
      setIsDirty(false);
      addNotification({
        title: t('notifications.successTitle'),
        message: t('notifications.settingsUpdated'),
        type: 'success',
      });
    } catch (error) {
      console.error('Failed to save settings', error);
      addNotification({
        title: t('common:errors.genericErrorTitle'),
        message: t('common:errors.genericErrorMessage'),
        type: 'error',
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancel = () => {
    if (isDirty) {
      if (window.confirm(t('settings:page.unsavedChangesPrompt'))) {
        setFormData(JSON.parse(JSON.stringify(initialFormData)));
        setIsDirty(false);
        navigate('/dashboard');
      }
    } else {
      navigate('/dashboard');
    }
  };

  const scrollToSection = (sectionId: string) => {
    setActiveSectionId(sectionId);
    sectionRefs.current[sectionId]?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  if (!currentUser || isLoading || !formData) {
    return <div className="p-6 text-center">{t('settings:page.loading')}</div>;
  }

  const userRole = currentUser.role;
  const showEditProfileButton = userRole !== UserRole.PARENT;

  const renderFoundationLayout = () => {
    const tabsConfig = availableSections.map(section => ({
      label: t(section.nameKey),
      icon: section.icon,
      content: (
        <div key={section.id}>
          <section.component settings={formData} onChange={handleFormChange} userRole={userRole} />
        </div>
      ),
    }));
    const activeTabIndex = availableSections.findIndex(section => section.id === activeSectionId);

    return (
      <Tabs
        tabs={tabsConfig}
        variant="line"
        activeTab={activeTabIndex > -1 ? activeTabIndex : 0}
        onTabChange={index => setActiveSectionId(availableSections[index].id)}
        className="flex-1 overflow-y-auto pt-3 sm:pt-4 lg:pt-6 px-3 sm:px-4 lg:px-8"
      />
    );
  };

  const renderDefaultLayout = () => (
    <div className="flex flex-col lg:flex-row flex-1 overflow-hidden">
      {/* Mobile/Tablet: Horizontal scrollable navigation */}
      <nav className="lg:hidden flex overflow-x-auto bg-white border-b border-gray-200 p-2 sm:p-3 space-x-2 flex-shrink-0">
        {availableSections.map(section => (
          <button
            key={section.id}
            onClick={() => scrollToSection(section.id)}
            className={`flex items-center px-3 py-2 text-xs sm:text-sm font-medium rounded-md whitespace-nowrap transition-colors
              ${activeSectionId === section.id
                ? 'bg-swiss-mint/10 text-swiss-mint'
                : 'text-gray-600 hover:bg-gray-100 hover:text-swiss-charcoal'
              }`}
            aria-current={activeSectionId === section.id ? 'page' : undefined}
          >
            <section.icon className={`w-4 h-4 sm:w-5 sm:h-5 mr-1.5 sm:mr-2 ${activeSectionId === section.id ? 'text-swiss-mint' : 'text-gray-400'}`} />
            {t(section.nameKey)}
          </button>
        ))}
      </nav>

      {/* Desktop: Vertical sidebar navigation */}
      <nav className="hidden lg:block w-56 xl:w-64 bg-white border-r border-gray-200 p-3 xl:p-4 space-y-1 overflow-y-auto flex-shrink-0">
        {availableSections.map(section => (
          <button
            key={section.id}
            onClick={() => scrollToSection(section.id)}
            className={`w-full flex items-center px-2.5 xl:px-3 py-2 xl:py-2.5 text-sm font-medium rounded-md text-left transition-colors
              ${activeSectionId === section.id
                ? 'bg-swiss-mint/10 text-swiss-mint'
                : 'text-gray-600 hover:bg-gray-100 hover:text-swiss-charcoal'
              }`}
            aria-current={activeSectionId === section.id ? 'page' : undefined}
          >
            <section.icon className={`w-4 h-4 xl:w-5 xl:h-5 mr-2 xl:mr-3 flex-shrink-0 ${activeSectionId === section.id ? 'text-swiss-mint' : 'text-gray-400'}`} />
            <span className="truncate">{t(section.nameKey)}</span>
          </button>
        ))}
      </nav>

      <main className="flex-1 overflow-y-auto p-3 sm:p-4 lg:p-6 xl:p-8 space-y-4 sm:space-y-6 lg:space-y-8 scroll-smooth">
        {availableSections.map(section => {
          const SectionComponent = section.component;
          return (
            <div key={section.id} id={section.id} ref={el => { sectionRefs.current[section.id] = el; }}>
              <SectionComponent settings={formData} onChange={handleFormChange} userRole={userRole} />
            </div>
          );
        })}
        {availableSections.length === 0 && (
          <Card className="p-4 sm:p-6 text-center">
            <p className="text-sm sm:text-base text-gray-600">{t('settings:page.noSectionsAvailable')}</p>
          </Card>
        )}
      </main>
    </div>
  );


  return (
    <div className="flex flex-col h-full bg-page-bg">
      <div className="sticky top-0 z-30 bg-page-bg/80 backdrop-blur-md px-3 sm:px-4 lg:px-8 py-2 sm:py-3 lg:py-4 border-b border-gray-200">
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-2 sm:gap-0">
          <h1 className="text-lg sm:text-xl lg:text-2xl font-bold text-swiss-charcoal">{t('settings:page.title')}</h1>
          <div className="flex items-center flex-wrap gap-2 sm:space-x-2 lg:space-x-3">
              {showEditProfileButton && (
                <Button
                  variant="outline"
                  leftIcon={PencilSquareIcon}
                  onClick={() => navigate('/settings/profile')}
                  className="text-xs sm:text-sm"
                >
                  {t('common:buttons.editProfile', 'Edit Profile')}
                </Button>
              )}
              {availableSections.length > 1 && (
                <>
                  <Button variant="light" onClick={handleCancel} className="text-xs sm:text-sm">
                    {t('common:buttons.cancel')}
                  </Button>
                  <Button
                    variant="primary"
                    onClick={handleSave}
                    disabled={!isDirty || isSaving}
                    className="bg-swiss-mint hover:bg-opacity-90 text-xs sm:text-sm"
                  >
                    {isSaving
                      ? `${t('common:buttons.saveChanges')}...`
                      : t('common:buttons.saveChanges')}
                  </Button>
                </>
              )}
          </div>
        </div>
      </div>

      {userRole === UserRole.FOUNDATION ? renderFoundationLayout() : renderDefaultLayout()}
    </div>
  );
};

export default SettingsPage;
